import { Sport } from './conference';
import { Game } from './game';
import { Team } from './team';

export interface QwikcutCamera {
	_id?: string;
	cameraId: string;
	label?: string;
	jetsonSerialNumber?: string;
	carrierBoardSerialNumber?: string;
	axisCameraSerialNumber?: string;
	poeSplitterSerialNumber?: string;
	poeInjectorSerialNumber?: string;
	ssdSerialNumber?: string;
	teamids?: Team['_id'][];
	socketId?: string;
	recordingGameid?: Game['_id'];
	previewPlaybackUrl?: string;
	currentSoftware?: QwikcutCameraSoftware[];
	updateChannel?: QwikcutCameraReleaseChannel;
	updateState?: QwikcutCameraUpdateState;
	zrokSsh?: QwikcutCameraZrokSsh;
	roiConfig?: RoiConfig;
	jetsonMacAddress?: string;
	createdAt?: Date;
	updatedAt?: Date;

	//not in db
	teams?: Team[];
	currentRecordingData?: QwikcutCameraCurrentRecordingData;
	upcomingRecordings?: Game[];
	loadingUpcomingRecordings?: boolean;
}

export type QwikcutCameraUpdateStatus = 'idle' | 'available' | 'downloading' | 'verified' | 'installing' | 'restarting' | 'healthy' | 'rolled_back' | 'failed';

export interface QwikcutCameraUpdateState {
	channel?: QwikcutCameraReleaseChannel;
	currentVersion?: string;
	targetVersion?: string;
	releaseId?: string;
	status?: QwikcutCameraUpdateStatus;
	lastMessage?: string;
	lastError?: string;
	lastReportedAt?: Date;
}

export interface QwikcutCameraZrokSsh {
	enabled?: boolean;
	shareToken?: string;
	backend?: string;
	serviceName?: string;
	lastReportedAt?: Date;
	lastError?: string;
}

export interface QwikcutCameraSoftware {
	_id?: string;
  name: string;
	type: QwikcutCameraSoftwareType;
	sportid?: Sport;
  version: number;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface QwikcutCameraCurrentRecordingData {
	gameid?: string;
	remoteRtmpUrl?: string;
	endTime?: Date;
	game?: Game;
}


export enum QwikcutCameraSoftwareType {
  project = 'project',
  visionApp = 'visionApp',
  model = 'model',
  config = 'config',
}

export const qwikcutCameraSoftwareTypeOptions: {value: QwikcutCameraSoftwareType, display: string}[] = [
	{value: QwikcutCameraSoftwareType.project, display: 'Node.JS Project'},
	{value: QwikcutCameraSoftwareType.model, display: 'Detection Model'},
	{value: QwikcutCameraSoftwareType.visionApp, display: 'Python Stitching/Tracking App'},
	{value: QwikcutCameraSoftwareType.config, display: 'Config File'}
];

export type QwikcutCameraReleaseStatus = 'draft' | 'published' | 'disabled';
export type QwikcutCameraReleaseChannel = 'production' | 'staging' | 'development';
export type QwikcutCameraReleaseType = 'app' | 'asset';
export type QwikcutCameraReleaseArtifactKind = 'app' | 'onnx' | 'labels' | 'engine';

export const qwikcutCameraReleaseChannelOptions: {value: QwikcutCameraReleaseChannel, display: string}[] = [
	{value: 'production', display: 'Production'},
	{value: 'staging', display: 'Staging'},
	{value: 'development', display: 'Development'},
];

export interface QwikcutCameraReleaseArtifact {
	kind: QwikcutCameraReleaseArtifactKind;
	sportid?: string;
	assetVersion?: string;
	filename: string;
	s3Filename?: string;
	s3Key: string;
	url: string;
	destination?: string;
	sha256: string;
	size?: number;
}

export interface QwikcutCameraReleaseTargets {
	cameraIds?: string[];
	excludeCameraIds?: string[];
}

export interface QwikcutCameraReleaseCompatibility {
	hardware?: {
		arch?: string;
		platform?: string;
		l4t?: string;
		jetpack?: string;
	};
	minAppVersion?: string;
	maxAppVersion?: string;
}

export interface QwikcutCameraRelease {
	_id?: string;
	version: string;
	releaseId: string;
	releaseType?: QwikcutCameraReleaseType;
	sportid?: string;
	assetKinds?: QwikcutCameraReleaseArtifactKind[];
	channel: QwikcutCameraReleaseChannel;
	status: QwikcutCameraReleaseStatus;
	sequence: number;
	notes?: string;
	bucket: string;
	prefix: string;
	manifestKey: string;
	manifestUrl: string;
	artifacts: QwikcutCameraReleaseArtifact[];
	manifest: any;
	compatibility?: QwikcutCameraReleaseCompatibility;
	targets?: QwikcutCameraReleaseTargets;
	createdBy?: string;
	publishedBy?: string;
	publishedAt?: Date;
	createdAt?: Date;
	updatedAt?: Date;
}

export interface QwikcutCameraUpdateEvent {
	_id?: string;
	cameraId: string;
	releaseId?: string;
	version?: string;
	channel?: QwikcutCameraReleaseChannel;
	status: QwikcutCameraUpdateStatus;
	message?: string;
	error?: string;
	reportedAt: Date;
	createdAt?: Date;
	updatedAt?: Date;
}

export enum CameraCommand {
	SHUTDOWN = 'shutdown',
	REBOOT = 'reboot',
	RESTART_SERVER = 'restart',
	CHECK_FOR_UPDATES = 'check_for_updates',
	UPLOAD_LOGS	= 'upload_logs',
	STOP_CAPTURE = 'stop_capture',
};

export interface RoiConfig {
	topLeft: RoiPoint;
	topCenter: RoiPoint;
	topRight: RoiPoint;
	bottomRight: RoiPoint;
	bottomRightCenter: RoiPoint;
	bottomLeftCenter: RoiPoint;
	bottomLeft: RoiPoint;
}

export interface RoiPoint {
	x: number;
	y: number;
}

export const getDefaultRoiConfig = (): RoiConfig => {
	return {
		topLeft: {x: 0.2, y: 0.2},
		topCenter: {x: 0.5, y: 0.2},
		topRight: {x: 0.8, y: 0.2},
		bottomRight: {x: 0.8, y: 0.8},
		bottomRightCenter: {x: 0.6, y: 0.8},
		bottomLeftCenter: {x: 0.4, y: 0.8},
		bottomLeft: {x: 0.2, y: 0.8}
	};
}

export interface ScoreboardData {
	homeTeam: string;
	homeScore: number;
	awayTeam: string;
	awayScore: number;
	period?: string;
}

export enum TrackingMode {
	tracking = 'tracking',
	full = 'full',
	center = 'center',
	left = 'left',
	right = 'right'
}

export const allTrackingModes: TrackingMode[] = [
	TrackingMode.tracking,
	TrackingMode.full,
	TrackingMode.center,
	TrackingMode.left,
	TrackingMode.right
];

export interface GameVideoFileGroup {
  gameid: string;
  clips: GameVideoFile[];

	game?: Game;
	uploading?: boolean;
}

export interface GameVideoFile {
  path: string;
  clipnumber: number;
  fileSize: number;
}
